// Grouping and date-range formatting for the series cart summary. Parameters are typed
// structurally so the Coalesce models and minimal test fixtures both satisfy them.

export interface SeriesCartPassDate {
  eventDate?: { eventStartDateTime?: Date | string | null } | null;
}

export interface SeriesCartPassType {
  id?: number | null;
  name?: string | null;
  order?: number | null;
  eventPrice?: { price?: number | null; serviceFee?: number | null } | null;
  passTypeDates?: SeriesCartPassDate[] | null;
}

export interface SeriesCartTicket {
  passTypeId?: number | null;
  price?: number | null;
  fees?: number | null;
}

export interface SeriesTicketGroup {
  passTypeId: number;
  passTypeName: string;
  passQty: number;
  passPrice: number;
  passFee: number;
  groupTotal: number;
  dateLabel: string;
}

/**
 * One cart row per PassType. Price and fee come from the priced tickets, not EventPrice:
 * the backend resolves the service fee (agent vs public, waived/free events, the tenant fee
 * snapshot winning over a non-overridden row) and can land on a value that differs from
 * EventPrice.serviceFee. Reading the charged amounts keeps this summary consistent with the
 * pass selector and the grand total instead of showing the public fee.
 */
export function buildSeriesTicketGroups(
  tickets: SeriesCartTicket[],
  passTypes: SeriesCartPassType[],
): SeriesTicketGroup[] {
  const groups = new Map<
    number,
    { passType: SeriesCartPassType; count: number; price: number; fees: number }
  >();
  for (const t of tickets) {
    if (t.passTypeId == null) continue;
    if (!groups.has(t.passTypeId)) {
      const pt = passTypes.find((p) => p.id === t.passTypeId);
      if (!pt) continue;
      groups.set(t.passTypeId, { passType: pt, count: 0, price: 0, fees: 0 });
    }
    const g = groups.get(t.passTypeId)!;
    g.count++;
    g.price += t.price ?? 0;
    g.fees += t.fees ?? 0;
  }

  return Array.from(groups.values())
    .map(({ passType, count, price, fees }) => {
      const datesPerPass = Math.max(1, passType.passTypeDates?.length ?? 1);
      const passQty = Math.floor(count / datesPerPass);
      // Per-pass amounts from the charged tickets; fall back to EventPrice only when the pass
      // has no priced tickets yet (passQty 0), which the rendered summary never lists.
      const passPrice =
        passQty > 0 ? price / passQty : (passType.eventPrice?.price ?? 0);
      const passFee =
        passQty > 0 ? fees / passQty : (passType.eventPrice?.serviceFee ?? 0);
      return {
        passTypeId: passType.id!,
        passTypeName: passType.name ?? "Pass",
        passQty,
        passPrice,
        passFee,
        groupTotal: price + fees,
        dateLabel: formatPassDateRange(passType.passTypeDates ?? []),
      };
    })
    .sort((a, b) => {
      const order = (id: number) =>
        passTypes.find((p) => p.id === id)?.order ?? 0;
      return order(a.passTypeId) - order(b.passTypeId);
    });
}

/** Compact date label for a pass: a single date, or the first and last dates as a range. */
export function formatPassDateRange(
  passTypeDates: SeriesCartPassDate[],
): string {
  const dates = passTypeDates
    .map((ptd) => ptd.eventDate?.eventStartDateTime)
    .filter((d): d is Date | string => d != null)
    .map((d) => new Date(d as any))
    .sort((a, b) => a.getTime() - b.getTime());
  if (dates.length === 0) return "";

  const first = dates[0];
  const monthDay = { month: "short", day: "numeric" } as const;
  const monthDayYear = {
    month: "short",
    day: "numeric",
    year: "numeric",
  } as const;
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString("en-US", opts);

  if (dates.length === 1) return fmt(first, monthDayYear);

  const last = dates[dates.length - 1];
  const sameYear = first.getFullYear() === last.getFullYear();
  const sameMonth = sameYear && first.getMonth() === last.getMonth();

  if (sameMonth) {
    return `${fmt(first, monthDay)}–${last.getDate()}, ${first.getFullYear()}`;
  }
  if (sameYear) {
    return `${fmt(first, monthDay)} – ${fmt(last, monthDay)}, ${first.getFullYear()}`;
  }
  return `${fmt(first, monthDayYear)} – ${fmt(last, monthDayYear)}`;
}

/** Series header range from a start/end pair, shared by the landing page and the purchase page. */
export function formatSeriesStartEndRange(
  start?: Date | string | null,
  end?: Date | string | null,
): string {
  if (!start) return "";
  const s = new Date(start as any);
  const e = end ? new Date(end as any) : s;
  const monthDayYearLong = {
    month: "long",
    day: "numeric",
    year: "numeric",
  } as const;
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString("en-US", opts);

  if (s.toDateString() === e.toDateString()) {
    return fmt(s, monthDayYearLong);
  }
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${fmt(s, { month: "long", day: "numeric" })}–${e.getDate()}, ${s.getFullYear()}`;
  }
  const monthDayYearShort = {
    month: "short",
    day: "numeric",
    year: "numeric",
  } as const;
  return `${fmt(s, monthDayYearShort)} – ${fmt(e, monthDayYearShort)}`;
}
